// OverviewArtifacts.tsx — the commit pane's artifacts: one row per artifact
// name, one column per selected commit (the metrics table's shape, so the
// same file from two commits sits on one row), and below it the open
// artifacts in their viewers, side by side.
//
// Opening a row opens that name at every selected commit that has it —
// the qualitative comparison this exists for. Viewers that benefit from it
// (splats) share one camera unless "Sync cameras" is switched off.
import { useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import { utils } from 'common';
import type { Artifact, CommitOverview } from '../api';
import ArtifactPanel from '../artifacts/ArtifactPanel';
import { CameraSync } from '../artifacts/cameraSync';
import { viewerFor } from '../artifacts/registry';
import { shortSha } from './format';

interface Props {
  commits: CommitOverview[];
}

interface Row {
  name: string;
  /** Artifacts at each commit sha; several when experiments overlap. */
  byCommit: Map<string, Artifact[]>;
}

export function buildArtifactRows(commits: CommitOverview[]): Row[] {
  const rows = new Map<string, Row>();
  for (const c of commits) {
    for (const a of c.artifacts ?? []) {
      let row = rows.get(a.name);
      if (!row) {
        row = { name: a.name, byCommit: new Map() };
        rows.set(a.name, row);
      }
      const list = row.byCommit.get(c.commit.sha) ?? [];
      list.push(a);
      row.byCommit.set(c.commit.sha, list);
    }
  }
  return Array.from(rows.values()).sort((a, b) => a.name.localeCompare(b.name));
}

export default function OverviewArtifacts({ commits }: Props) {
  const rows = buildArtifactRows(commits);
  // Open artifacts by id, in the order they were opened.
  const [open, setOpen] = useState<number[]>([]);
  const [synced, setSynced] = useState(true);
  const sync = useMemo(() => (synced ? new CameraSync() : null), [synced]);

  const all = new Map<number, Artifact>();
  for (const c of commits) for (const a of c.artifacts ?? []) all.set(a.id, a);
  // Columns follow the selection order; an artifact whose commit was
  // deselected closes with it.
  const order = new Map(commits.map((c, i) => [c.commit.sha, i]));
  const shown = open
    .map((id) => all.get(id))
    .filter((a): a is Artifact => !!a)
    .sort((a, b) => (order.get(a.commit_sha) ?? 0) - (order.get(b.commit_sha) ?? 0));
  const comparable = shown.length > 1 && shown.some((a) => viewerFor(a.mime_type).comparable);

  if (rows.length === 0) {
    return (
      <div className="text-secondary">
        No artifacts on the selected commit{commits.length === 1 ? '' : 's'}.
      </div>
    );
  }

  const toggle = (ids: number[]): void => {
    const allOpen = ids.every((id) => open.includes(id));
    setOpen(allOpen ? open.filter((id) => !ids.includes(id)) : [...open, ...ids.filter((id) => !open.includes(id))]);
  };

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Artifact</th>
              {commits.map((c) => (
                <th key={c.commit.sha}><code>{shortSha(c.commit.sha)}</code></th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const ids = Array.from(row.byCommit.values()).flat().map((a) => a.id);
              return (
                <tr key={row.name}>
                  <td>
                    <code>{row.name}</code>
                    {ids.length > 1 && (
                      <Button variant="link" size="sm" className="ms-2 p-0 align-baseline" onClick={() => toggle(ids)}>
                        {ids.every((id) => open.includes(id)) ? 'Close all' : 'Compare'}
                      </Button>
                    )}
                  </td>
                  {commits.map((c) => {
                    const arts = row.byCommit.get(c.commit.sha);
                    if (!arts) {
                      return <td key={c.commit.sha} className="text-secondary">—</td>;
                    }
                    return (
                      <td key={c.commit.sha}>
                        {arts.map((a) => (
                          <div key={a.id} title={`${a.mime_type} · ${a.experiment} · sha256 ${a.sha256}`}>
                            <Button variant="link" size="sm" className="p-0 align-baseline" onClick={() => toggle([a.id])}>
                              {open.includes(a.id) ? 'Close' : 'Open'}
                            </Button>
                            <span className="text-secondary ms-2">
                              {utils.formatBytes(a.size)}
                              {arts.length > 1 ? ` · ${a.experiment}` : ''}
                            </span>
                          </div>
                        ))}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {shown.length > 0 && (
        <>
          {comparable && (
            <div className="form-check form-switch mb-2">
              <input
                id="artifacts-sync"
                className="form-check-input"
                type="checkbox"
                checked={synced}
                onChange={(e) => setSynced(e.target.checked)}
              />
              <label htmlFor="artifacts-sync" className="form-check-label text-secondary">
                Sync cameras
              </label>
            </div>
          )}
          <div
            style={{
              display: 'grid',
              gap: '1rem',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 24rem), 1fr))',
            }}
          >
            {shown.map((a) => (
              <ArtifactPanel
                key={`${a.id}:${a.sha256}`}
                artifact={a}
                sync={viewerFor(a.mime_type).comparable ? sync : null}
                caption={a.name}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
